<template>
  <label class="form-input">
    <span class="label" v-if="label"
      >{{ label }}
      <span v-if="required" class="form-input__required">*</span>
    </span>
    <span
      class="form-input__control"
      :class="{
        'form-input__control--icon': icon,
        'form-input__control--action': isPassword,
      }"
    >
      <i
        v-if="icon"
        class="form-input__icon"
        :class="icon"
        aria-hidden="true"
      ></i>
      <component
        ref="refInput"
        :is="customComponent"
        v-bind="customProps"
        @input="setValue"
      />
      <button
        v-if="isPassword"
        type="button"
        class="form-input__action"
        tabindex="-1"
        :aria-label="
          revealed ? $t('common.hidePassword') : $t('common.showPassword')
        "
        @click="toggleReveal"
      >
        <i
          :class="revealed ? 'uil uil-eye-slash' : 'uil uil-eye'"
          aria-hidden="true"
        ></i>
      </button>
    </span>
  </label>
</template>

<script>
import CustomSelect from "@/components/Form/AppCustomSelect";
import CustomNumberInput from "@/components/Form/AppNumberInput";
import AppTextarea from "@/components/Form/AppTextarea";
import AppMonthInput from "@/components/Form/AppMonthInput";
import TextInput from "@/components/Form/AppTextInput";
import { shallowRef, onMounted, reactive, computed, ref, nextTick } from "vue";

export default {
  props: {
    type: {
      type: String,
      default: "text",
    },
    name: {
      type: String,
    },
    label: {
      type: String,
    },
    icon: {
      type: String,
      default: null,
    },
    options: {
      type: Array,
    },
    rows: {
      type: Number,
    },
    pattern: {
      type: String,
    },
    value: {
      type: String,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    maxlength: {
      type: [String, Number],
      default: null,
    },
    exactlength: {
      type: [String, Number],
      default: null,
    },
    exactValue: {
      type: [String, Number],
      default: null,
    },
  },
  setup(props, { emit }) {
    const refInput = ref(null);
    const customComponent = shallowRef(null);
    const reactiveProps = reactive(props);
    const revealed = ref(false);
    const componentDefinition = {
      input: [
        "type",
        "name",
        "pattern",
        "value",
        "disabled",
        "required",
        "exactValue",
        "maxlength",
        "readonly",
      ],
      number: [
        "name",
        "value",
        "disabled",
        "required",
        "exactlength",
        "maxlength",
        "min",
        "max",
      ],
      month: ["name", "required", "disabled"],
      select: ["name", "options", "disabled", "value"],
      textarea: ["name", "rows"],
    };

    const components = {
      input: TextInput,
      number: CustomNumberInput,
      month: AppMonthInput,
      select: CustomSelect,
      textarea: AppTextarea,
    };

    const getComponentType = (originalType) => {
      const inputTypes = ["text", "email", "password"];
      if (inputTypes.includes(originalType)) {
        return "input";
      } else {
        return originalType;
      }
    };

    const isPassword = computed(() => props.type === "password");

    const customProps = computed(() => {
      const type = getComponentType(props.type);
      const componentProps = componentDefinition[type];
      const obj = {};
      componentProps.forEach((key) => {
        // Undefined entries are skipped on purpose: Vue re-syncs a `value` prop
        // to the DOM on every patch without comparing it first, so forwarding
        // `value: undefined` wipes whatever the user typed as soon as the input
        // re-renders (toggling password visibility, flipping `disabled`, ...).
        if (reactiveProps[key] !== undefined) {
          obj[key] = reactiveProps[key];
        }
      });
      if (isPassword.value) {
        obj.type = revealed.value ? "text" : "password";
      }
      return obj;
    });

    onMounted(() => {
      const type = getComponentType(props.type);
      customComponent.value = components[type];
    });

    const toggleReveal = () => {
      revealed.value = !revealed.value;
      // The button steals focus on click, so hand it back to the field the user
      // was typing in (the caret keeps its position).
      nextTick(() => {
        refInput.value?.textInput?.focus();
      });
    };

    const setValue = (e) => {
      let value = typeof e == "object" ? e.target.value : e;
      // console.log("*****");
      // console.log(value);
      // console.log("*****");
      emit("update:value", value);
    };

    return {
      setValue,
      customComponent,
      customProps,
      refInput,
      isPassword,
      revealed,
      toggleReveal,
    };
  },
};
</script>

<style lang="scss" scoped>
.form-input {
  @apply inline-block;
  @apply w-full;
  &__required {
    @apply text-red-400;
  }

  &__control {
    @apply relative;
    @apply block;
    @apply mb-4;

    .input {
      @apply mb-0;
    }
  }

  &__control--icon .input {
    @apply pl-10;
  }

  &__control--action .input {
    @apply pr-11;
  }

  &__icon {
    @apply absolute;
    @apply left-3;
    @apply text-lg;
    @apply leading-none;
    @apply text-bluegray-400;
    @apply pointer-events-none;
    @apply transition-colors;
    @apply duration-200;
    top: 50%;
    transform: translateY(-50%);
  }

  &__control:focus-within .form-input__icon {
    @apply text-gignetblue-400;
  }

  &__action {
    // Tailwind's base layer sets `-webkit-appearance: button` and lands after
    // the component styles in the production bundle. Without an explicit
    // background the browser then paints its native button chrome (the OS
    // accent colour), which only disappeared on hover because that state does
    // set a background.
    -webkit-appearance: none;
    appearance: none;
    @apply bg-transparent;
    @apply border-0;
    @apply absolute;
    @apply right-1;
    @apply h-8;
    @apply w-9;
    @apply flex;
    @apply items-center;
    @apply justify-center;
    @apply rounded-md;
    @apply text-lg;
    @apply leading-none;
    @apply text-bluegray-400;
    @apply outline-none;
    @apply transition-colors;
    @apply duration-200;
    top: 50%;
    transform: translateY(-50%);

    &:hover {
      @apply text-bluegray-600;
      @apply bg-bluegray-100;
    }

    &:focus-visible {
      @apply text-gignetblue-400;
      @apply bg-bluegray-100;
    }
  }
}

.input {
  @apply mb-3;
  @apply block;
  @apply w-full;
  @apply rounded-lg;
  @apply bg-white;
  @apply border-bluegray-200;
  @apply shadow-sm;
  @apply px-3;
  @apply py-2.5;
  @apply text-bluegray-700;
  @apply text-sm;
  @apply transition-all;
  @apply duration-200;
  @apply outline-none;

  &::placeholder {
    @apply text-bluegray-300;
  }

  &:hover:not(:disabled):not(:focus) {
    @apply border-bluegray-300;
  }

  &:focus:invalid {
    @apply border-red-300;
    @apply ring-red-200;
  }
  &:focus {
    @apply border-gignetblue-300;
    @apply ring;
    @apply ring-gignetblue-200;
    @apply ring-opacity-50;
    @apply ring-offset-0;
  }

  &.disabled,
  &:disabled {
    @apply bg-bluegray-50;
    @apply text-bluegray-400;
    @apply border-bluegray-200;
    @apply cursor-not-allowed;
    @apply shadow-none;
  }
}

// .invalid {
//   @apply border-red-300;
//   @apply shadow-red-300-md;
// }

.label {
  @apply text-bluegray-500;
  @apply uppercase;
  @apply text-xs;
  @apply font-semibold;
  @apply mb-2;
  @apply inline-block;
  @apply tracking-wider;
}
</style>
